'use client';

import { motion } from 'framer-motion';
import { Check, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
  type ButtonHTMLAttributes,
  type FC,
  forwardRef,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BaseLineDuration } from '@/app/components/icons/BaseLineDuration';
import { BaseLineFreeCrop } from '@/app/components/icons/BaseLineFreeCrop';
import { ProductLine1 } from '@/app/components/icons/ProductLine1';
import { ProductLine3 } from '@/app/components/icons/ProductLine3';
import { ProductLine4 } from '@/app/components/icons/ProductLine4';
import { ProductLine9 } from '@/app/components/icons/ProductLine9';
import { ProductLine16 } from '@/app/components/icons/ProductLine16';
import { ProductLine21 } from '@/app/components/icons/ProductLine21';
import { ProductLineAmount } from '@/app/components/icons/ProductLineAmount';
import { ProductOriginalRatio } from '@/app/components/icons/ProductOriginalRatio';
import { CustomAspectRatioDialog } from '@/app/components/shared/CustomAspectRatioDialog';
import { DurationGridSelect } from '@/app/components/shared/DurationGridSelect';
import {
  ModelVersionRemainingTrialsBadge,
  ModelVersionSelectItemContent,
} from '@/app/components/shared/ModelVersionSelectItemContent';
import { SettingRadioGroup } from '@/app/components/shared/SettingRadioGroup';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValueWithImageOnly,
} from '@/app/components/ui/select';
import { toast } from '@/app/components/ui/use-toast';
import { MODEL_VERSION, MODEL_VERSION_SETTINGS_MAP } from '@/app/constants';
import {
  type CodecOptionsResult,
  useAudioLipSyncConstraint,
  useCodecOptions,
  useMembershipRevision,
  useModelItems,
  useUpgradeDialogs,
} from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ModelVersion, ReferencePrompt, SubsPlanType, TaskSettings } from '@/app/types';
import {
  CUSTOM_ASPECT_RATIO_VALUE,
  cn,
  generateFromPage,
  getAvailableOptions,
  getModelCapabilityKey,
  getModelVersionRemainingTrials,
  getPromptMaxLength,
  isCustomAspectRatioValue,
  isOptionAllowed,
  parseCustomAspectRatio,
  reportEvent,
  syncImageReferencePrompts,
  validateAndCorrectSettings,
} from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import { HOME_SELECT_ITEM_CLASS_NAME, HomeSelectHighlight } from './HomeSelectHighlight';

const isQ4HighResolution = (version: ModelVersion, resolution?: string) =>
  version === MODEL_VERSION.Q4 && (resolution === '2k' || resolution === '4k');

import { useDelayedSelectClose } from './hooks/use-delayed-select-close';
import {
  getHomeDesktopSettingsLayout,
  getHomeMobileSettingsLayout,
  getVisibleHomeSettingKeys,
  HOME_DROPDOWN_CONTENT_CLASS_NAME,
  type HomeGenerationMode,
  type HomeGenerationSettingKey,
  toFormTabType,
} from './utils';

export type HomeSettingKey = HomeGenerationSettingKey;

interface HomeSettingOption {
  value: string;
  label: ReactNode;
  showVip: boolean;
  remainingTrials?: number;
  showVipWithRemainingTrials?: boolean;
  disabled?: boolean;
  tooltip?: ReactNode;
}

export interface HomeVisibleSetting {
  key: HomeSettingKey;
  title: ReactNode;
  icon: ReactNode;
  value: string;
  displayValue: ReactNode;
  options: HomeSettingOption[];
}

interface HomeGenerationSettingsBarProps {
  mode: HomeGenerationMode;
  settings?: TaskSettings;
  onUpdateSettings: (newValues: Partial<TaskSettings>) => void;
  className?: string;
}

const mergeCommonProperties = (target: TaskSettings, source: TaskSettings): TaskSettings => {
  const targetKeys = new Set<keyof TaskSettings>(Object.keys(target) as (keyof TaskSettings)[]);
  const commonProperties = Object.fromEntries(
    (Object.keys(source) as (keyof TaskSettings)[])
      .filter((key) => targetKeys.has(key))
      .map((key) => [key, source[key]]),
  );

  return {
    ...target,
    ...commonProperties,
  };
};

/**
 * store 里的 duration 归一化成 select 的字符串值。
 *
 * `fallback` 传当前模型的首个可用时长：0 / undefined / NaN 这类非法值不能统一洗成
 * 'auto'——只有 Q2 Pro 的时长选项里有 auto，Q3 洗成 auto 会显示成一个它根本不支持的
 * 「智能」。口径与 validateAndCorrectSettings 的 availableValues[0] 保持一致。
 */
export const normalizeHomeDurationValue = (value?: number | string, fallback = '') => {
  // Q2 Pro 的「智能」在 store 里存 -1
  if (value === -1) return 'auto';
  if (!value) return fallback;

  return String(value);
};

const getHomeAudioValue = (settings?: TaskSettings) => {
  if (!settings?.with_audio) {
    return 'none';
  }

  return settings.lip_sync ? 'sfx_voice' : 'sfx';
};

const isHomeImageGenerationMode = (mode: HomeGenerationMode) => mode === 'reference2image' || mode === 'text2image';

const getHomeOptionPermissionKey = (
  key: HomeSettingKey,
  mode?: HomeGenerationMode,
): Parameters<typeof isOptionAllowed>[0] | undefined => {
  switch (key) {
    case 'duration':
    case 'resolution':
    case 'schedule_mode':
    case 'transition':
    case 'codec':
      return key;
    case 'sample_count':
      // 图片生成页允许免费用户直接选择 1-4 张；数量权限只适用于视频生成。
      return mode && isHomeImageGenerationMode(mode) ? undefined : key;
    case 'style_preference':
      return 'transition';
    default:
      return undefined;
  }
};

const HomeRemainingTrialsBadge = ({ remainingTrials, className }: { remainingTrials: number; className?: string }) => {
  const t = useTranslations();

  return (
    <span
      className={cn(
        'inline-flex w-max shrink-0 whitespace-nowrap rounded-4 bg-[rgba(76,99,255,0.48)] p-1 text-[10px] text-system-white leading-[1]',
        className,
      )}
    >
      {t('create.createForm.settings.freeTrials', {
        count: remainingTrials,
        s: remainingTrials > 1 ? 's' : '',
        es: remainingTrials > 1 ? 'es' : '',
      })}
    </span>
  );
};

interface HomeTrialRemainingState {
  remainingTrials720p?: number;
  remainingTrialsQ1?: number;
  remainingTrialsQ2TransitionSpeed?: number;
  remainingTrialsQ2TransitionPro?: number;
  remainingTrialsQ2Pro?: number;
  remainingTrialsQ2T2V?: number;
  remainingTrialsQ2C2V?: number;
  remainingTrialsQ2T2I?: number;
  remainingTrialsQ2R2I?: number;
  remainingTrialsImage25T2IFast?: number;
  remainingTrialsImage25T2IPro?: number;
  remainingTrialsImage25R2IFast?: number;
  remainingTrialsImage25R2IPro?: number;
  remainingTrialsQ3T2VI2V?: number;
  remainingTrialsQ3C2V?: number;
  trialsQ3T2VI2VDurations?: number[];
  trialsQ3C2VDurations?: number[];
}

const getPositiveRemainingTrials = (remainingTrials?: number) =>
  typeof remainingTrials === 'number' && remainingTrials > 0 ? remainingTrials : undefined;

const supportsAudioLipSyncConstraint = (mode: HomeGenerationMode, modelVersion: ModelVersion) =>
  mode === 'character2video' && (modelVersion === MODEL_VERSION.Q2 || modelVersion === MODEL_VERSION.Q2_PRO);

const getHomeSettingOptionTrialInfo = ({
  key,
  value,
  mode,
  modelVersion,
  userPlan,
  img2videoValidImageCount,
  trials,
}: {
  key: HomeSettingKey;
  value: string;
  mode: HomeGenerationMode;
  modelVersion: ModelVersion;
  userPlan: SubsPlanType;
  img2videoValidImageCount: number;
  trials: HomeTrialRemainingState;
}): Pick<HomeSettingOption, 'remainingTrials'> => {
  if (key === 'resolution') {
    if (value === '720p' && userPlan === 'free' && !isOptionAllowed('resolution', value, 'free', modelVersion)) {
      return { remainingTrials: getPositiveRemainingTrials(trials.remainingTrials720p) };
    }

    if (value === '1080p' && modelVersion === MODEL_VERSION.Q1 && mode !== 'reference2image') {
      return { remainingTrials: getPositiveRemainingTrials(trials.remainingTrialsQ1) };
    }
  }

  if (
    key === 'transition' &&
    mode === 'img2video' &&
    modelVersion === MODEL_VERSION.Q2 &&
    img2videoValidImageCount <= 2
  ) {
    if (value === 'speed') {
      return { remainingTrials: getPositiveRemainingTrials(trials.remainingTrialsQ2TransitionSpeed) };
    }

    if (value === 'pro') {
      return {
        remainingTrials: getPositiveRemainingTrials(trials.remainingTrialsQ2TransitionPro),
      };
    }
  }

  return {};
};

const isHomeSettingTrialOptionAvailable = (params: Parameters<typeof getHomeSettingOptionTrialInfo>[0]) =>
  !!getHomeSettingOptionTrialInfo(params).remainingTrials;

const isVipVisualOption = (option: Pick<HomeSettingOption, 'showVip' | 'remainingTrials'>) =>
  option.showVip || Boolean(option.remainingTrials);

const getHomeSettingOptions = (
  key: HomeSettingKey,
  availableOptions: ReturnType<typeof getAvailableOptions>,
  codecOptions: CodecOptionsResult['options'],
): readonly string[] => {
  if (key === 'audio_and_lip_sync') {
    return ['none', 'sfx', 'sfx_voice'];
  }

  if (key === 'codec') {
    return codecOptions.map((option) => option.value);
  }

  if (key === 'style_preference') {
    return availableOptions.transition?.length ? availableOptions.transition : ['creative', 'stable'];
  }

  if (key === 'style') {
    return availableOptions.style?.length ? availableOptions.style : ['general', 'anime'];
  }

  if (key === 'movement_amplitude') {
    return availableOptions.movement_amplitude?.length
      ? availableOptions.movement_amplitude
      : ['auto', 'small', 'medium', 'large'];
  }

  return availableOptions[key] || [];
};

const getHomeSettingValue = (key: HomeSettingKey, settings: TaskSettings | undefined, fallback: string) => {
  switch (key) {
    case 'duration':
      return normalizeHomeDurationValue(settings?.duration, fallback);
    case 'sample_count':
      return String(settings?.sample_count ?? 1);
    case 'audio_and_lip_sync':
      return getHomeAudioValue(settings);
    case 'style_preference':
      return String(settings?.transition ?? fallback);
    default:
      return String(settings?.[key] ?? fallback);
  }
};

export const getHomeSettingOptionLabel = (
  key: HomeSettingKey,
  value: string,
  t: ReturnType<typeof useTranslations>,
  /**
   * 当前生效的自定义宽高比（如 "4:5"）。只在 `custom` 选项上用来把已填的比例回显成
   * 「自定义(4:5)」，与参考生表单页的 AspectRatioOption 保持同一套文案。
   */
  customAspectRatio?: string,
) => {
  if (key === 'duration') {
    return value === 'auto'
      ? t('create.createForm.settings.duration.option.auto')
      : t('create.createForm.settings.duration.option.text', { time: value });
  }

  if (key === 'resolution') {
    const resolutionMap: Record<string, ReactNode> = {
      '512': t('create.createForm.settings.resolution.option512.text'),
      '720p': t('create.createForm.settings.resolution.option720p.text'),
      '1080p': t('create.createForm.settings.resolution.option1080p.text'),
    };

    return resolutionMap[value] ?? value.toUpperCase();
  }

  if (key === 'aspect_ratio' && value === 'auto') {
    return t('create.createForm.settings.aspectRatio.optionAuto.text');
  }

  if (key === 'aspect_ratio' && value === 'intelligent') {
    return t('create.createForm.settings.aspectRatio.optionIntelligent.text');
  }

  if (key === 'aspect_ratio' && value === CUSTOM_ASPECT_RATIO_VALUE) {
    return customAspectRatio
      ? t('create.createForm.settings.aspectRatio.optionCustom.textWithValue', { value: customAspectRatio })
      : t('create.createForm.settings.aspectRatio.optionCustom.text');
  }

  if (key === 'sample_count') {
    return value;
  }

  if (key === 'transition') {
    const transitionMap: Record<string, ReactNode> = {
      speed: t('create.createForm.settings.creationMode.optionFlash.text'),
      pro: t('create.createForm.settings.creationMode.optionCinematic.text'),
      pro_ad: t('create.createForm.settings.creationMode.optionProAd.text'),
    };

    return transitionMap[value] ?? value;
  }

  if (key === 'style_preference') {
    const stylePreferenceMap: Record<string, ReactNode> = {
      creative: t('create.createForm.settings.stylePreference.optionCreative.text'),
      stable: t('create.createForm.settings.stylePreference.optionStable.text'),
    };

    return stylePreferenceMap[value] ?? value;
  }

  if (key === 'style') {
    const styleMap: Record<string, ReactNode> = {
      general: t('create.createForm.settings.style.optionGeneral.text'),
      anime: t('create.createForm.settings.style.optionAnime.text'),
    };

    return styleMap[value] ?? value;
  }

  if (key === 'movement_amplitude') {
    const movementMap: Record<string, ReactNode> = {
      auto: t('create.createForm.settings.movementAmplitude.optionAuto.text'),
      small: t('create.createForm.settings.movementAmplitude.optionSmall.text'),
      medium: t('create.createForm.settings.movementAmplitude.optionMedium.text'),
      large: t('create.createForm.settings.movementAmplitude.optionLarge.text'),
    };

    return movementMap[value] ?? value;
  }

  if (key === 'schedule_mode') {
    return value === 'nopeak'
      ? t('create.createForm.settings.nonPeakMode.title')
      : t('home.generationForm.settings.normalMode');
  }

  if (key === 'codec') {
    const codecMap: Record<string, ReactNode> = {
      h265: t('create.createForm.settings.codec.optionH265.text'),
      h264: t('create.createForm.settings.codec.optionH264.text'),
      h265_pro: t('create.createForm.settings.codec.optionH265Pro.text'),
      h264_pro: t('create.createForm.settings.codec.optionH264Pro.text'),
    };

    return codecMap[value] ?? value.toUpperCase();
  }

  if (key === 'audio_and_lip_sync') {
    const audioMap: Record<string, ReactNode> = {
      none: t('create.createForm.settings.sound.optionNoDubbing.text'),
      sfx: t('create.createForm.settings.sound.optionSfxOnly.text'),
      sfx_voice: t('create.createForm.settings.sound.optionSfxVoice.text'),
    };

    return audioMap[value] ?? value;
  }

  return value;
};

export const getHomeSettingTitle = (key: HomeSettingKey, t: ReturnType<typeof useTranslations>) => {
  const titleMap: Record<HomeSettingKey, ReactNode> = {
    duration: t('create.createForm.settings.duration.title'),
    resolution: t('create.createForm.settings.resolution.title'),
    aspect_ratio: t('create.createForm.settings.aspectRatio.title'),
    sample_count: t('create.createForm.settings.amount.title'),
    transition: t('create.createForm.settings.creationMode.title'),
    style_preference: t('create.createForm.settings.stylePreference.title'),
    movement_amplitude: t('create.createForm.settings.movementAmplitude.title'),
    style: t('create.createForm.settings.style.title'),
    schedule_mode: t('create.createForm.settings.nonPeakMode.title'),
    codec: t('create.createForm.settings.codec.title'),
    audio_and_lip_sync: t('create.createForm.settings.sound.title'),
  };

  return titleMap[key];
};

const ASPECT_RATIO_ICON_MAP: Record<string, ReactNode> = {
  '16:9': <ProductLine16 />,
  '9:16': <ProductLine9 />,
  '21:9': <ProductLine21 />,
  '9:21': <ProductLine21 className="rotate-90" />,
  '1:1': <ProductLine1 />,
  '4:3': <ProductLine4 />,
  '3:4': <ProductLine3 />,
  auto: <ProductOriginalRatio />,
  intelligent: <ProductOriginalRatio />,
  [CUSTOM_ASPECT_RATIO_VALUE]: <BaseLineFreeCrop />,
};
const getHomeSettingIcon = (key: HomeSettingKey, value: string): ReactNode => {
  if (key === 'duration') {
    return <BaseLineDuration />;
  }

  if (key === 'resolution') {
    return <span className="text-[11px] leading-none">HD</span>;
  }

  if (key === 'aspect_ratio') {
    return ASPECT_RATIO_ICON_MAP[value] ?? <ProductOriginalRatio />;
  }

  if (key === 'sample_count') {
    return <ProductLineAmount />;
  }

  return <SlidersHorizontal className="size-4" />;
};

export const getHomeDurationGridOptions = (setting: HomeVisibleSetting) =>
  setting.options.map((option) => ({
    value: option.value,
    label: option.value === 'auto' ? option.label : option.value === setting.value ? `${option.value}s` : option.value,
    isVipOption: isVipVisualOption(option),
  }));

const HOME_SETTINGS_POPOVER_HORIZONTAL_INSETS = 26;
const HOME_SETTINGS_OPTION_MIN_WIDTH = 64;
const HOME_SETTINGS_ASPECT_RATIO_OPTION_MIN_WIDTH = 68;
const HOME_SETTINGS_RESOLUTION_OPTION_MIN_WIDTH = 92;
const HOME_SETTINGS_CODEC_OPTION_MIN_WIDTH = 112;

export const getHomeSettingsPopoverWidth = (settings: HomeVisibleSetting[], baseWidth = 360) => {
  const maxOptionsWidth = settings.reduce((maxWidth, setting) => {
    const optionMinWidth =
      setting.key === 'aspect_ratio'
        ? HOME_SETTINGS_ASPECT_RATIO_OPTION_MIN_WIDTH
        : setting.key === 'resolution'
          ? HOME_SETTINGS_RESOLUTION_OPTION_MIN_WIDTH
          : setting.key === 'codec'
            ? HOME_SETTINGS_CODEC_OPTION_MIN_WIDTH
            : HOME_SETTINGS_OPTION_MIN_WIDTH;

    return Math.max(maxWidth, setting.options.length * optionMinWidth);
  }, 0);

  return Math.max(baseWidth, maxOptionsWidth + HOME_SETTINGS_POPOVER_HORIZONTAL_INSETS);
};

export const getHomeVisibleSettings = ({
  mode,
  modelVersion,
  settings,
  availableOptions,
  userPlan,
  trials,
  img2videoValidImageCount = 0,
  isSfxVoiceDisabled = false,
  isNewMembership = false,
  t,
  codecOptions,
}: {
  mode: HomeGenerationMode;
  modelVersion: ModelVersion;
  settings?: TaskSettings;
  availableOptions: ReturnType<typeof getAvailableOptions>;
  userPlan: SubsPlanType;
  trials: HomeTrialRemainingState;
  img2videoValidImageCount?: number;
  isSfxVoiceDisabled?: boolean;
  isNewMembership?: boolean;
  t: ReturnType<typeof useTranslations>;
  codecOptions: CodecOptionsResult['options'];
}): HomeVisibleSetting[] =>
  getHomeVisibleSettingKeysForSettingsBar({
    mode,
    modelVersion,
    resolution: settings?.resolution,
    availableOptions,
    img2videoValidImageCount,
  }).reduce<HomeVisibleSetting[]>((result, key) => {
    const options = getHomeSettingOptions(key, availableOptions, codecOptions);
    if (!options.length) {
      return result;
    }

    const value = getHomeSettingValue(key, settings, options[0] ?? '');
    // codec 之外，duration 也必须回退：把不在 options 里的值交给 Radix Select 当受控 value，
    // 它会回吐一次 onValueChange('')，空串最终被 parseInt 成 NaN 写回 store。
    const fallbackNormalizedValue =
      (key === 'codec' || key === 'duration') && !options.includes(value) ? (options[0] ?? value) : value;
    // 用户填的自定义宽高比（如 "4:5"）不在 options 里，选中态要落在 `custom` 这个占位选项上，
    // 真实比例只用于文案回显。
    const customAspectRatio =
      key === 'aspect_ratio' && options.includes(CUSTOM_ASPECT_RATIO_VALUE) && isCustomAspectRatioValue(value, options)
        ? value
        : undefined;
    const normalizedValue = customAspectRatio ? CUSTOM_ASPECT_RATIO_VALUE : fallbackNormalizedValue;
    const permissionKey = getHomeOptionPermissionKey(key, mode);

    result.push({
      key,
      title: getHomeSettingTitle(key, t),
      icon: getHomeSettingIcon(key, normalizedValue),
      value: normalizedValue,
      displayValue: getHomeSettingOptionLabel(key, normalizedValue, t, customAspectRatio),
      options: options.map((option) => {
        const trialInfo = getHomeSettingOptionTrialInfo({
          key,
          value: option,
          mode,
          modelVersion,
          userPlan,
          img2videoValidImageCount,
          trials,
        });

        return {
          value: option,
          label: getHomeSettingOptionLabel(key, option, t, customAspectRatio),
          disabled: key === 'audio_and_lip_sync' && option === 'sfx_voice' ? isSfxVoiceDisabled : undefined,
          tooltip:
            key === 'audio_and_lip_sync' && option === 'sfx_voice' && isSfxVoiceDisabled
              ? t('create.createForm.settings.sound.optionSfxVoice.disabledTip2')
              : undefined,
          showVip:
            (permissionKey ? !isOptionAllowed(permissionKey, option, 'free', modelVersion, isNewMembership) : false) &&
            !trialInfo.remainingTrials,
          ...trialInfo,
        };
      }),
    });

    return result;
  }, []);

export const getHomeVisibleSettingKeysForSettingsBar = ({
  mode,
  modelVersion,
  resolution,
  availableOptions,
  img2videoValidImageCount = 0,
}: {
  mode: HomeGenerationMode;
  modelVersion: ModelVersion;
  resolution?: string;
  availableOptions: ReturnType<typeof getAvailableOptions>;
  img2videoValidImageCount?: number;
}): HomeGenerationSettingKey[] => {
  const keys = getVisibleHomeSettingKeys(mode, modelVersion);

  if (mode !== 'img2video') {
    return isQ4HighResolution(modelVersion, resolution) ? keys.filter((key) => key !== 'schedule_mode') : keys;
  }

  if (img2videoValidImageCount >= 3) {
    // Multi-frame img2video follows the standalone form: it is forced onto Q2
    // and the off-peak queue is unavailable. Keep both controls out of the
    // homepage settings surface (duration is not supported either).
    return keys.filter((key) => key !== 'duration' && key !== 'schedule_mode');
  }

  if (isQ4HighResolution(modelVersion, resolution)) {
    return keys.filter((key) => key !== 'schedule_mode');
  }

  if (!availableOptions.duration?.length || keys.includes('duration')) {
    return keys;
  }

  return [...keys, 'duration'];
};

const getHomeSettingChangePatch = ({
  key,
  value,
  settings,
  modelVersion,
  type,
  t,
}: {
  key: HomeSettingKey;
  value: string;
  settings?: TaskSettings;
  modelVersion: ModelVersion;
  type: ReturnType<typeof toFormTabType>;
  t: ReturnType<typeof useTranslations>;
}): Partial<TaskSettings> | undefined => {
  if (key === 'duration') {
    if (value === 'auto') {
      return { duration: -1 };
    }

    // Radix Select 拿到不在 options 里的受控 value 时会回吐一次 onValueChange('')，
    // 空串 parseInt 出来是 NaN。不校验就会把 NaN 写进 store，覆盖掉用户真正选的时长。
    const parsedDuration = Number.parseInt(value, 10);
    if (!Number.isFinite(parsedDuration)) {
      return undefined;
    }

    if (modelVersion === MODEL_VERSION['2.0'] && settings?.resolution !== '720p' && parsedDuration === 8) {
      toast({
        message: t('create.createForm.settings.duration.unSupportToast'),
      });
      return { duration: 8, resolution: '720p' };
    }

    return { duration: parsedDuration };
  }

  if (key === 'resolution') {
    if (modelVersion === MODEL_VERSION['2.0'] && value !== '720p' && settings?.duration === 8) {
      toast({
        message: t('create.createForm.settings.resolution.unSupportToast'),
      });
      return { resolution: value, duration: 4 };
    }

    return { resolution: value };
  }

  if (key === 'aspect_ratio') {
    // 兜底：`custom` 只是「打开自定义弹窗」的占位选项，不是合法比例，写进 settings 会一路
    // 带到提交参数。正常路径上 useHomeCustomAspectRatio 已在调用这里之前拦掉了，这层是防
    // 止将来新增调用点绕过那个拦截。
    if (value === CUSTOM_ASPECT_RATIO_VALUE) {
      return undefined;
    }

    return { aspect_ratio: value };
  }

  if (key === 'sample_count') {
    return { sample_count: Number.parseInt(value, 10) };
  }

  if (key === 'transition' || key === 'style_preference') {
    const corrected = validateAndCorrectSettings({
      version: modelVersion,
      newSettings: { ...settings, transition: value } as TaskSettings,
      type,
      t,
    });

    return {
      transition: corrected.transition,
      duration: corrected.duration,
      resolution: corrected.resolution,
    };
  }

  if (key === 'audio_and_lip_sync') {
    const nextSettings: Partial<TaskSettings> =
      value === 'none'
        ? { with_audio: false, lip_sync: false }
        : value === 'sfx'
          ? { with_audio: true, lip_sync: false }
          : { with_audio: true, lip_sync: true };

    if (settings?.schedule_mode === 'nopeak') {
      nextSettings.schedule_mode = 'normal';
      toast({
        type: 'info',
        message: t('popup.toast.invalidSettingForOffPeakMode'),
      });
    }

    return nextSettings;
  }

  if (key === 'schedule_mode') {
    const nextSettings: Partial<TaskSettings> = { schedule_mode: value as TaskSettings['schedule_mode'] };
    if (value === 'nopeak' && settings?.with_audio) {
      nextSettings.with_audio = false;
      nextSettings.lip_sync = false;
      toast({
        type: 'info',
        message: t('popup.toast.invalidSettingForOffPeakMode'),
      });
    }

    return nextSettings;
  }

  if (key === 'movement_amplitude') {
    return { movement_amplitude: value as TaskSettings['movement_amplitude'] };
  }

  if (key === 'codec') {
    return { codec: value as TaskSettings['codec'] };
  }

  if (key === 'style') {
    return { style: value };
  }

  return undefined;
};

/**
 * 首页自定义宽高比的开关逻辑。
 *
 * 首页的宽高比是 SettingRadioGroup 单选项，不像参考生表单页的 CommonSelect 有
 * `onItemClick` 钩子，所以「点 custom 开弹窗」只能在 onChange 里拦下来：`custom` 本身
 * 不是合法比例，写进 settings 会连带污染提交参数。
 */
const useHomeCustomAspectRatio = ({
  settings,
  onUpdateSettings,
}: {
  settings?: TaskSettings;
  onUpdateSettings: (newValues: Partial<TaskSettings>) => void;
}) => {
  const [open, setOpen] = useState(false);
  const { wide, high } = parseCustomAspectRatio(settings?.aspect_ratio);

  return {
    dialog: (
      <CustomAspectRatioDialog
        open={open}
        onOpenChange={setOpen}
        onConfirm={(nextWide, nextHigh) => onUpdateSettings({ aspect_ratio: `${nextWide}:${nextHigh}` })}
        initialWide={wide}
        initialHigh={high}
      />
    ),
    /** 命中 custom 时返回 true，表示该次点击已被弹窗接管，调用方不要再写 settings。 */
    handleSettingChange: (key: HomeSettingKey, value: string) => {
      if (key !== 'aspect_ratio' || value !== CUSTOM_ASPECT_RATIO_VALUE) {
        return false;
      }

      setOpen(true);
      return true;
    },
  };
};

const HomeSettingsPopover = ({
  trigger,
  children,
  contentClassName,
  settings,
  baseWidth,
}: {
  trigger: ReactNode;
  children: ReactNode;
  contentClassName?: string;
  settings?: HomeVisibleSetting[];
  baseWidth?: number;
}) => {
  const width = settings ? getHomeSettingsPopoverWidth(settings, baseWidth) : undefined;

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={8}
        collisionPadding={16}
        style={width ? { width } : undefined}
        className={cn(
          'z-[41] max-w-[calc(100vw-32px)] border border-white/[0.08] p-3 text-system-white',
          HOME_DROPDOWN_CONTENT_CLASS_NAME,
          contentClassName,
        )}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
};

export const HomeSettingTrigger = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    setting?: HomeVisibleSetting;
    label?: ReactNode;
    icon?: ReactNode;
    showIcon?: boolean;
    showChevron?: boolean;
    /**
     * 用 framer-motion 的 layout 动画给按钮宽度变化（如切换宽高比/数量/清晰度后文案变长变短）
     * 加平滑过渡。仅按需开启：宽度会随内容变化的触发器才需要，纯图标或固定宽度按钮不需要。
     */
    animateWidth?: boolean;
  }
>(
  (
    {
      setting,
      label,
      icon,
      showIcon = true,
      showChevron = true,
      animateWidth = false,
      className,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const triggerIcon = icon ?? setting?.icon;
    const triggerLabel = label ?? setting?.displayValue;
    const triggerClassName = cn(
      'flex h-9 min-w-0 shrink-0 items-center gap-2 rounded-10 border border-transparent bg-white/[0.04] px-3 text-system-white transition-colors hover:border-white/[0.12] hover:bg-white/[0.08] data-[state=open]:border-white/[0.12] data-[state=open]:bg-white/[0.08]',
      className,
    );
    const content = (
      <>
        {showIcon && triggerIcon ? <span className="shrink-0 text-base text-system-white64">{triggerIcon}</span> : null}
        {triggerLabel ? <span className="min-w-0 truncate text-[13px]">{triggerLabel}</span> : null}
        {showChevron ? <ChevronDown className="size-3 shrink-0 text-system-white48" /> : null}
      </>
    );

    if (animateWidth) {
      // motion.button 的事件 prop 类型（onDrag/onAnimationStart 等）与原生 button 不兼容，
      // 但调用点从未传这些字段，运行时透传是安全的，这里用 any 绕开类型声明冲突。
      const motionProps = props as Record<string, unknown>;
      return (
        <motion.button
          ref={ref}
          type={type}
          layout
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={triggerClassName}
          {...motionProps}
        >
          {content}
        </motion.button>
      );
    }

    return (
      <button ref={ref} type={type} className={triggerClassName} {...props}>
        {content}
      </button>
    );
  },
);
HomeSettingTrigger.displayName = 'HomeSettingTrigger';

const HomeSettingOptions = ({
  setting,
  onChange,
}: {
  setting: HomeVisibleSetting;
  onChange: (key: HomeSettingKey, value: string) => void;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 px-1 text-system-white32 text-xs">
        <span>{setting.title}</span>
      </div>
      <div className="-mt-3 hide-scrollbar min-w-0 overflow-x-auto pt-3">
        <SettingRadioGroup
          value={setting.value}
          options={setting.options.map((option) => ({
            value: option.value,
            label: option.label,
            isVipOption: isVipVisualOption(option),
            disabled: option.disabled,
            tooltip: option.tooltip,
            badgeEle: option.remainingTrials ? (
              <HomeRemainingTrialsBadge
                remainingTrials={option.remainingTrials}
                className="-top-3 -right-1 absolute z-[100] ml-0 scale-[0.8] px-[6px] py-1 text-xs"
              />
            ) : undefined,
          }))}
          onValueChange={(value) => onChange(setting.key, value)}
          activeMode="item"
          className="rounded-10 bg-white/[0.04] p-0"
          optionsWrapperClassName={cn(
            'min-w-[64px] text-system-white40',
            setting.key === 'aspect_ratio' ? 'h-[68px]' : 'h-11',
            setting.key === 'resolution' && 'min-w-[92px]',
            setting.key === 'codec' && 'min-w-[112px]',
          )}
          activeOptionClassName="rounded-10 border border-white/[0.18] bg-white/[0.14] text-system-white"
          labelClassName="text-system-white40"
          // 宽高比是图标 + 文字竖排，本身不需要左右内边距，去掉可以收窄整个 popover。
          labelBaseClassName={setting.key === 'aspect_ratio' ? 'px-0' : undefined}
          renderContent={(option, isActive) => {
            return (
              <span
                className={cn(
                  'flex min-w-0 items-center justify-center gap-1',
                  setting.key === 'aspect_ratio' && 'flex-col gap-2',
                )}
              >
                {setting.key === 'aspect_ratio' ? (
                  <span className={cn('text-[22px]', isActive ? 'text-system-white' : 'text-system-white32')}>
                    {getHomeSettingIcon(setting.key, option.value)}
                  </span>
                ) : null}
                <span className={cn('truncate', isActive && 'font-semibold')}>{option.label}</span>
              </span>
            );
          }}
        />
      </div>
    </div>
  );
};

export const HomeGenerationSettingsBar: FC<HomeGenerationSettingsBarProps> = ({
  mode,
  settings,
  onUpdateSettings,
  className,
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const { handleOpenPlanDialog } = useUpgradeDialogs();
  const isNewMembership = useMembershipRevision();
  const type = toFormTabType(mode);
  const modelItems = useModelItems(type);
  const { options: codecOptions } = useCodecOptions();

  const {
    modelVersion,
    formParams,
    updateFormParams,
    updateModelVersion,
    isLoggedIn,
    userPlan,
    hiddenReferencePrompts,
    setHiddenReferencePrompts,
    remainingTrials720p,
    remainingTrialsQ1,
    remainingTrialsQ2TransitionSpeed,
    remainingTrialsQ2TransitionPro,
    remainingTrialsQ2Pro,
    remainingTrialsQ2T2V,
    remainingTrialsQ2C2V,
    remainingTrialsQ2T2I,
    remainingTrialsQ2R2I,
    remainingTrialsImage25T2IFast,
    remainingTrialsImage25T2IPro,
    remainingTrialsImage25R2IFast,
    remainingTrialsImage25R2IPro,
    remainingTrialsQ3T2VI2V,
    remainingTrialsQ3C2V,
    trialsQ3T2VI2VDurations,
    trialsQ3C2VDurations,
    img2videoValidImageCount,
    characterReferencePrompts,
  } = useStore((state) => ({
    modelVersion: state[`${type}FormParams`].modelVersion,
    formParams: state[`${type}FormParams`],
    updateFormParams: state.updateFormParams,
    updateModelVersion: state.updateModelVersion,
    isLoggedIn: Boolean(state.userInfo),
    userPlan: (state.userInfo?.subs_plan ?? 'free') as SubsPlanType,
    hiddenReferencePrompts: state.hiddenReferencePrompts,
    setHiddenReferencePrompts: state.setHiddenReferencePrompts,
    remainingTrials720p: state.trials720p?.remaining,
    remainingTrialsQ1: state.trialsQ1?.remaining,
    remainingTrialsQ2TransitionSpeed: state.trialsQ2TransitionSpeed?.remaining,
    remainingTrialsQ2TransitionPro: state.trialsQ2TransitionPro?.remaining,
    remainingTrialsQ2Pro: state.trialsQ2Pro?.remaining,
    remainingTrialsQ2T2V: state.trialsQ2T2V?.remaining,
    remainingTrialsQ2C2V: state.trialsQ2C2V?.remaining,
    remainingTrialsQ2T2I: state.trialsQ2T2I?.remaining,
    remainingTrialsQ2R2I: state.trialsQ2R2I?.remaining,
    remainingTrialsImage25T2IFast: state.trialsImage25T2IFast?.remaining,
    remainingTrialsImage25T2IPro: state.trialsImage25T2IPro?.remaining,
    remainingTrialsImage25R2IFast: state.trialsImage25R2IFast?.remaining,
    remainingTrialsImage25R2IPro: state.trialsImage25R2IPro?.remaining,
    remainingTrialsQ3T2VI2V: state.trialsQ3T2VI2V?.remaining,
    remainingTrialsQ3C2V: state.trialsQ3C2V?.remaining,
    trialsQ3T2VI2VDurations: state.trialsQ3T2VI2V?.durations,
    trialsQ3C2VDurations: state.trialsQ3C2V?.durations,
    img2videoValidImageCount: state.img2videoFormParams.imagePrompts.filter(Boolean).length,
    characterReferencePrompts: state.character2videoFormParams.referencePrompts,
  }));
  const { isSfxVoiceDisabled } = useAudioLipSyncConstraint(
    supportsAudioLipSyncConstraint(mode, modelVersion)
      ? (characterReferencePrompts as ReferencePrompt[] | undefined)
      : undefined,
  );

  useEffect(() => {
    if (mode !== 'character2video' || !isSfxVoiceDisabled || !settings?.with_audio || !settings.lip_sync) {
      return;
    }

    onUpdateSettings({ with_audio: true, lip_sync: false });
    toast({ message: t('create.createForm.settings.sound.optionSfxVoice.autoSwitchTip2') });
  }, [isSfxVoiceDisabled, mode, onUpdateSettings, settings?.lip_sync, settings?.with_audio, t]);

  const trials = useMemo(
    (): HomeTrialRemainingState => ({
      remainingTrials720p,
      remainingTrialsQ1,
      remainingTrialsQ2TransitionSpeed,
      remainingTrialsQ2TransitionPro,
      remainingTrialsQ2Pro,
      remainingTrialsQ2T2V,
      remainingTrialsQ2C2V,
      remainingTrialsQ2T2I,
      remainingTrialsQ2R2I,
      remainingTrialsImage25T2IFast,
      remainingTrialsImage25T2IPro,
      remainingTrialsImage25R2IFast,
      remainingTrialsImage25R2IPro,
      remainingTrialsQ3T2VI2V,
      remainingTrialsQ3C2V,
      trialsQ3T2VI2VDurations,
      trialsQ3C2VDurations,
    }),
    [
      remainingTrials720p,
      remainingTrialsQ1,
      remainingTrialsQ2TransitionSpeed,
      remainingTrialsQ2TransitionPro,
      remainingTrialsQ2Pro,
      remainingTrialsQ2T2V,
      remainingTrialsQ2C2V,
      remainingTrialsQ2T2I,
      remainingTrialsQ2R2I,
      remainingTrialsImage25T2IFast,
      remainingTrialsImage25T2IPro,
      remainingTrialsImage25R2IFast,
      remainingTrialsImage25R2IPro,
      remainingTrialsQ3T2VI2V,
      remainingTrialsQ3C2V,
      trialsQ3T2VI2VDurations,
      trialsQ3C2VDurations,
    ],
  );
  const availableOptions = getAvailableOptions(modelVersion, type, {
    transition: settings?.transition,
    resolution: settings?.resolution,
  });
  const currentModelRemainingTrials = getModelVersionRemainingTrials({
    type,
    modelVersion,
    settings,
    trials,
  });
  const selectedModelItem = modelItems.find((item) => item.value === modelVersion);
  const modelSelectListRef = useRef<HTMLDivElement | null>(null);
  const handleModelVersionChange = (nextModelVersion: ModelVersion) => {
    const prevSettingsKey = MODEL_VERSION_SETTINGS_MAP[modelVersion];
    const nextSettingsKey = MODEL_VERSION_SETTINGS_MAP[nextModelVersion];
    const prevSettings = formParams[prevSettingsKey];
    const nextSettings = formParams[nextSettingsKey];
    const newSettings = validateAndCorrectSettings({
      version: nextModelVersion,
      newSettings: mergeCommonProperties(nextSettings, prevSettings),
      t,
      type,
    }) as TaskSettings;

    updateFormParams(type, nextSettingsKey, newSettings);
    if (mode === 'character2video') {
      const updates = syncImageReferencePrompts({
        prevVersion: modelVersion,
        nextVersion: nextModelVersion,
        formParams,
        locale,
        hiddenReferencePrompts,
      });
      if (updates.referencePrompts) {
        updateFormParams('character2video', 'referencePrompts', updates.referencePrompts);
      }
      if (updates.textPromptWithReference) {
        updateFormParams('character2video', 'textPromptWithReference', updates.textPromptWithReference);
      }
      if (updates.imagePrompts) {
        updateFormParams('character2video', 'imagePrompts', updates.imagePrompts);
      }
      if (updates.textPrompt !== undefined) {
        updateFormParams('character2video', 'textPrompt', updates.textPrompt);
      }
      if (updates.hiddenItems !== undefined) {
        setHiddenReferencePrompts(getModelCapabilityKey(modelVersion), updates.hiddenItems);
      }
    }
    if (updateModelVersion(type, nextModelVersion)) {
      toast({
        type: 'warning',
        message: t('create.createForm.error.invalidLength', { limit: getPromptMaxLength(nextModelVersion) }),
      });
    }
    reportEvent('model_version_switch_click', {
      version_result: nextModelVersion,
      interaction_position: 'home_generation_form',
    });
  };
  const modelSelectState = useDelayedSelectClose((value) => handleModelVersionChange(value as ModelVersion));
  const customAspectRatio = useHomeCustomAspectRatio({ settings, onUpdateSettings });

  const handleSettingChange = (key: HomeSettingKey, value: string) => {
    // A queued/stale popover event must not re-enable off-peak scheduling after
    // the third frame is added. Standalone img2video hides this option entirely
    // in multi-frame mode and forces the normal queue.
    if (key === 'schedule_mode' && mode === 'img2video' && img2videoValidImageCount >= 3) {
      if (settings?.schedule_mode !== 'normal') {
        onUpdateSettings({ schedule_mode: 'normal' });
      }
      return;
    }

    const permissionKey = getHomeOptionPermissionKey(key, mode);
    const hasRemainingTrials = isHomeSettingTrialOptionAvailable({
      key,
      value,
      mode,
      modelVersion,
      userPlan,
      img2videoValidImageCount,
      trials,
    });
    if (
      permissionKey &&
      ((permissionKey === 'schedule_mode' && value === 'nopeak' && !isLoggedIn) ||
        !isOptionAllowed(permissionKey, value, userPlan, modelVersion, isNewMembership)) &&
      !hasRemainingTrials
    ) {
      handleOpenPlanDialog(generateFromPage({ modelVersion, type, key: permissionKey, value }));
      return;
    }

    if (customAspectRatio.handleSettingChange(key, value)) {
      return;
    }

    const nextSettings = getHomeSettingChangePatch({
      key,
      value,
      settings,
      modelVersion,
      type,
      t,
    });
    if (nextSettings) {
      if (key === 'resolution' && isQ4HighResolution(modelVersion, value)) {
        nextSettings.schedule_mode = 'normal';
      }
      onUpdateSettings(nextSettings);
    }
  };

  const visibleSettings = getHomeVisibleSettings({
    mode,
    modelVersion,
    settings,
    availableOptions,
    userPlan,
    trials,
    img2videoValidImageCount,
    isSfxVoiceDisabled,
    isNewMembership,
    t,
    codecOptions,
  });
  const settingsByKey = new Map(visibleSettings.map((setting) => [setting.key, setting]));
  const desktopSettingsLayout = getHomeDesktopSettingsLayout(visibleSettings.map((setting) => setting.key));
  const primarySettings = desktopSettingsLayout.primary
    .map((key) => settingsByKey.get(key))
    .filter((setting): setting is HomeVisibleSetting => !!setting);
  const compositeSettings = desktopSettingsLayout.composite
    .map((key) => settingsByKey.get(key))
    .filter((setting): setting is HomeVisibleSetting => !!setting);
  const durationSetting = desktopSettingsLayout.duration
    ? settingsByKey.get(desktopSettingsLayout.duration)
    : undefined;
  const durationSelectListRef = useRef<HTMLDivElement | null>(null);
  const durationSettingKeyRef = useRef(durationSetting?.key);
  durationSettingKeyRef.current = durationSetting?.key;
  const durationSelectState = useDelayedSelectClose((value) => {
    if (durationSettingKeyRef.current) {
      handleSettingChange(durationSettingKeyRef.current, value);
    }
  });
  const extraSettings = desktopSettingsLayout.extra
    .map((key) => settingsByKey.get(key))
    .filter((setting): setting is HomeVisibleSetting => !!setting);
  const compositeLabel = (
    <span className="flex min-w-0 items-center gap-3 overflow-hidden">
      {compositeSettings.map((setting, index) => (
        <span key={setting.key} className="flex min-w-0 items-center gap-3">
          <span className="truncate">{setting.displayValue}</span>
          {index < compositeSettings.length - 1 ? (
            <span aria-hidden="true" className="h-[18px] w-px shrink-0 bg-[rgba(255,255,255,0.08)]" />
          ) : null}
        </span>
      ))}
    </span>
  );

  return (
    <div className={cn('flex min-w-0 shrink-0 flex-nowrap items-center gap-2', className)}>
      <div className="w-auto shrink-0">
        <Select
          value={modelVersion}
          onValueChange={modelSelectState.onValueChange}
          open={modelSelectState.open}
          onOpenChange={modelSelectState.onOpenChange}
        >
          <SelectTrigger className="h-9 w-auto gap-2 rounded-10 border border-transparent bg-white/[0.04] px-2 text-system-white transition-colors hover:border-white/[0.12] hover:bg-white/[0.08]">
            <SelectValueWithImageOnly
              selectedItem={selectedModelItem}
              className="text-[13px]"
              activityTip={
                currentModelRemainingTrials ? (
                  <ModelVersionRemainingTrialsBadge remainingTrials={currentModelRemainingTrials} />
                ) : undefined
              }
            />
          </SelectTrigger>
          <SelectContent
            className={cn(
              'relative z-[41] w-max min-w-[200px] max-w-[calc(100vw-32px)] border border-white/[0.08] p-1',
              HOME_DROPDOWN_CONTENT_CLASS_NAME,
            )}
            side="bottom"
          >
            <div ref={modelSelectListRef} className="relative">
              <HomeSelectHighlight containerRef={modelSelectListRef} activeValue={modelVersion} className="rounded-8" />
              <SelectGroup className="flex flex-col gap-1">
                {modelItems.map((item) => (
                  <SelectItem
                    key={item.value}
                    value={item.value}
                    className={cn(HOME_SELECT_ITEM_CLASS_NAME, 'rounded-8 p-2 before:rounded-8')}
                  >
                    <ModelVersionSelectItemContent
                      image={item.image}
                      title={item.title}
                      description={item.description}
                      isNew={item.isNew}
                    />
                  </SelectItem>
                ))}
              </SelectGroup>
            </div>
          </SelectContent>
        </Select>
      </div>

      {primarySettings.map((setting) => (
        <HomeSettingsPopover
          key={setting.key}
          settings={[setting]}
          baseWidth={320}
          contentClassName="w-[320px]"
          trigger={
            <HomeSettingTrigger setting={setting} showIcon={false} showChevron={false} className="max-w-[160px]" />
          }
        >
          <HomeSettingOptions setting={setting} onChange={handleSettingChange} />
        </HomeSettingsPopover>
      ))}

      {compositeSettings.length > 0 ? (
        <HomeSettingsPopover
          settings={compositeSettings}
          baseWidth={280}
          contentClassName="w-[280px]"
          trigger={
            <HomeSettingTrigger
              label={compositeLabel}
              showIcon={false}
              showChevron={false}
              animateWidth={true}
              className="max-w-[260px]"
            />
          }
        >
          <div className="flex flex-col gap-3">
            {compositeSettings.map((setting) => (
              <HomeSettingOptions key={setting.key} setting={setting} onChange={handleSettingChange} />
            ))}
          </div>
        </HomeSettingsPopover>
      ) : null}

      {durationSetting ? (
        <DurationGridSelect
          value={durationSetting.value}
          options={getHomeDurationGridOptions(durationSetting)}
          onValueChange={durationSelectState.onValueChange}
          open={durationSelectState.open}
          onOpenChange={durationSelectState.onOpenChange}
          listRef={durationSelectListRef}
          itemClassName={HOME_SELECT_ITEM_CLASS_NAME}
          highlight={<HomeSelectHighlight containerRef={durationSelectListRef} activeValue={durationSetting.value} />}
          contentClassName="z-[41]"
          cols={Math.min(durationSetting.options.length, 8)}
          trigger={<HomeSettingTrigger setting={durationSetting} showChevron={false} />}
        />
      ) : null}

      {extraSettings.length > 0 ? (
        <HomeSettingsPopover
          settings={extraSettings}
          baseWidth={280}
          contentClassName="w-[280px]"
          trigger={
            <HomeSettingTrigger
              aria-label={t('home.more')}
              icon={<SlidersHorizontal className="size-4" />}
              showChevron={false}
              className="w-9 justify-center px-0"
            />
          }
        >
          <div className="flex flex-col gap-3">
            {extraSettings.map((setting) => (
              <HomeSettingOptions key={setting.key} setting={setting} onChange={handleSettingChange} />
            ))}
          </div>
        </HomeSettingsPopover>
      ) : null}

      {customAspectRatio.dialog}
    </div>
  );
};

export const HomeGenerationMobileSettingsSheetContent = ({
  mode,
  settings,
  onUpdateSettings,
  onClose,
}: HomeGenerationSettingsBarProps & {
  onClose: () => void;
}) => {
  const t = useTranslations();
  const { handleOpenPlanDialog } = useUpgradeDialogs();
  const isNewMembership = useMembershipRevision();
  const type = toFormTabType(mode);
  const { options: codecOptions } = useCodecOptions();
  const {
    modelVersion,
    isLoggedIn,
    userPlan,
    remainingTrials720p,
    remainingTrialsQ1,
    remainingTrialsQ2TransitionSpeed,
    remainingTrialsQ2TransitionPro,
    img2videoValidImageCount,
    characterReferencePrompts,
  } = useStore((state) => ({
    modelVersion: state[`${type}FormParams`].modelVersion,
    isLoggedIn: Boolean(state.userInfo),
    userPlan: (state.userInfo?.subs_plan ?? 'free') as SubsPlanType,
    remainingTrials720p: state.trials720p?.remaining,
    remainingTrialsQ1: state.trialsQ1?.remaining,
    remainingTrialsQ2TransitionSpeed: state.trialsQ2TransitionSpeed?.remaining,
    remainingTrialsQ2TransitionPro: state.trialsQ2TransitionPro?.remaining,
    img2videoValidImageCount: state.img2videoFormParams.imagePrompts.filter(Boolean).length,
    characterReferencePrompts: state.character2videoFormParams.referencePrompts,
  }));
  const { isSfxVoiceDisabled } = useAudioLipSyncConstraint(
    supportsAudioLipSyncConstraint(mode, modelVersion)
      ? (characterReferencePrompts as ReferencePrompt[] | undefined)
      : undefined,
  );
  const trials = useMemo(
    (): HomeTrialRemainingState => ({
      remainingTrials720p,
      remainingTrialsQ1,
      remainingTrialsQ2TransitionSpeed,
      remainingTrialsQ2TransitionPro,
    }),
    [remainingTrials720p, remainingTrialsQ1, remainingTrialsQ2TransitionSpeed, remainingTrialsQ2TransitionPro],
  );
  const availableOptions = getAvailableOptions(modelVersion, type, {
    transition: settings?.transition,
    resolution: settings?.resolution,
  });
  const visibleSettings = getHomeVisibleSettings({
    mode,
    modelVersion,
    settings,
    availableOptions,
    userPlan,
    trials,
    img2videoValidImageCount,
    isSfxVoiceDisabled,
    isNewMembership,
    t,
    codecOptions,
  });
  const settingsByKey = new Map(visibleSettings.map((setting) => [setting.key, setting]));
  const mobileSettings = getHomeMobileSettingsLayout(visibleSettings.map((setting) => setting.key))
    .map((key) => settingsByKey.get(key))
    .filter((setting): setting is HomeVisibleSetting => !!setting);

  const customAspectRatio = useHomeCustomAspectRatio({ settings, onUpdateSettings });

  const handleSettingChange = (key: HomeSettingKey, value: string) => {
    // Keep mobile sheet behavior aligned with the standalone multi-frame form.
    if (key === 'schedule_mode' && mode === 'img2video' && img2videoValidImageCount >= 3) {
      if (settings?.schedule_mode !== 'normal') {
        onUpdateSettings({ schedule_mode: 'normal' });
      }
      return;
    }

    const permissionKey = getHomeOptionPermissionKey(key, mode);
    const hasRemainingTrials = isHomeSettingTrialOptionAvailable({
      key,
      value,
      mode,
      modelVersion,
      userPlan,
      img2videoValidImageCount,
      trials,
    });
    if (
      permissionKey &&
      ((permissionKey === 'schedule_mode' && value === 'nopeak' && !isLoggedIn) ||
        !isOptionAllowed(permissionKey, value, userPlan, modelVersion, isNewMembership)) &&
      !hasRemainingTrials
    ) {
      onClose();
      handleOpenPlanDialog(generateFromPage({ modelVersion, type, key: permissionKey, value }));
      return;
    }

    if (customAspectRatio.handleSettingChange(key, value)) {
      return;
    }

    const nextSettings = getHomeSettingChangePatch({
      key,
      value,
      settings,
      modelVersion,
      type,
      t,
    });
    if (nextSettings) {
      onUpdateSettings(nextSettings);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col text-system-white">
      <div className="relative flex h-8 shrink-0 items-center justify-center">
        <button
          type="button"
          className="absolute right-0 flex h-8 items-center justify-center rounded-full px-1 text-sm text-system-white40"
          onClick={onClose}
        >
          {t('elements.elementDialog.close')}
        </button>
        <div className="font-semibold text-base">{t('create.createForm.settings.title')}</div>
      </div>
      <div className="mt-5 flex flex-1 flex-col gap-5 overflow-y-auto">
        {mobileSettings.map((setting) => (
          <div key={setting.key} className="flex flex-col gap-2">
            <div className="flex items-center gap-2 text-system-white48 text-xs">
              <span>{setting.title}</span>
            </div>
            <SettingRadioGroup
              value={setting.value}
              options={setting.options.map((option) => ({
                value: option.value,
                label: option.label,
                isVipOption: isVipVisualOption(option),
                disabled: option.disabled,
                tooltip: option.tooltip,
                badgeEle: option.remainingTrials ? (
                  <HomeRemainingTrialsBadge
                    remainingTrials={option.remainingTrials}
                    className="-top-3 -right-1 absolute z-[100] ml-0 scale-[0.8] px-[6px] py-1 text-xs"
                  />
                ) : undefined,
              }))}
              onValueChange={(value) => handleSettingChange(setting.key, value)}
              activeMode="item"
              className="rounded-10 bg-white/[0.04] p-0"
              optionsWrapperClassName={cn(
                'min-w-[64px] text-system-white40',
                setting.key === 'aspect_ratio' ? 'h-[68px]' : 'h-12',
                setting.key === 'resolution' && 'min-w-[104px]',
                setting.key === 'codec' && 'min-w-[112px]',
              )}
              activeOptionClassName="rounded-10 border border-white/[0.18] bg-white/[0.18] text-system-white"
              labelClassName="text-system-white40"
              renderContent={(option, isActive) => (
                <span
                  className={cn(
                    'flex min-w-0 items-center justify-center gap-1',
                    setting.key === 'aspect_ratio' && 'flex-col gap-2',
                  )}
                >
                  {setting.key === 'aspect_ratio' ? (
                    <span className={cn('text-[22px]', isActive ? 'text-system-white' : 'text-system-white32')}>
                      {getHomeSettingIcon(setting.key, option.value)}
                    </span>
                  ) : null}
                  <span className={cn('truncate', isActive && 'font-semibold')}>{option.label}</span>
                </span>
              )}
            />
          </div>
        ))}
      </div>
      {customAspectRatio.dialog}
    </div>
  );
};

export const HomeGenerationMobileModelSheetContent = ({
  mode,
  onClose,
}: Pick<HomeGenerationSettingsBarProps, 'mode' | 'settings'> & {
  onClose: () => void;
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const type = toFormTabType(mode);
  const modelItems = useModelItems(type);

  const {
    modelVersion,
    formParams,
    updateFormParams,
    updateModelVersion,
    hiddenReferencePrompts,
    setHiddenReferencePrompts,
  } = useStore((state) => ({
    modelVersion: state[`${type}FormParams`].modelVersion,
    formParams: state[`${type}FormParams`],
    updateFormParams: state.updateFormParams,
    updateModelVersion: state.updateModelVersion,
    hiddenReferencePrompts: state.hiddenReferencePrompts,
    setHiddenReferencePrompts: state.setHiddenReferencePrompts,
  }));

  const handleModelVersionChange = (nextModelVersion: ModelVersion) => {
    const prevSettingsKey = MODEL_VERSION_SETTINGS_MAP[modelVersion];
    const nextSettingsKey = MODEL_VERSION_SETTINGS_MAP[nextModelVersion];
    const prevSettings = formParams[prevSettingsKey];
    const nextSettings = formParams[nextSettingsKey];
    const newSettings = validateAndCorrectSettings({
      version: nextModelVersion,
      newSettings: mergeCommonProperties(nextSettings, prevSettings),
      t,
      type,
    }) as TaskSettings;

    updateFormParams(type, nextSettingsKey, newSettings);
    if (mode === 'character2video') {
      const updates = syncImageReferencePrompts({
        prevVersion: modelVersion,
        nextVersion: nextModelVersion,
        formParams,
        locale,
        hiddenReferencePrompts,
      });
      if (updates.referencePrompts) {
        updateFormParams('character2video', 'referencePrompts', updates.referencePrompts);
      }
      if (updates.textPromptWithReference) {
        updateFormParams('character2video', 'textPromptWithReference', updates.textPromptWithReference);
      }
      if (updates.imagePrompts) {
        updateFormParams('character2video', 'imagePrompts', updates.imagePrompts);
      }
      if (updates.textPrompt !== undefined) {
        updateFormParams('character2video', 'textPrompt', updates.textPrompt);
      }
      if (updates.hiddenItems !== undefined) {
        setHiddenReferencePrompts(getModelCapabilityKey(modelVersion), updates.hiddenItems);
      }
    }
    if (updateModelVersion(type, nextModelVersion)) {
      toast({
        type: 'warning',
        message: t('create.createForm.error.invalidLength', { limit: getPromptMaxLength(nextModelVersion) }),
      });
    }
    reportEvent('model_version_switch_click', {
      version_result: nextModelVersion,
      interaction_position: 'home_generation_form',
    });
    onClose();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col text-system-white">
      <div className="relative flex h-8 shrink-0 items-center justify-center">
        <button
          type="button"
          className="absolute right-0 flex h-8 items-center justify-center rounded-full px-1 text-sm text-system-white40"
          onClick={onClose}
        >
          {t('elements.elementDialog.close')}
        </button>
        <div className="font-semibold text-base">{t('home.generationForm.settings.modelTitle')}</div>
      </div>
      <div className="mt-5 flex flex-1 flex-col gap-3 overflow-y-auto">
        {modelItems.map((item) => {
          const active = item.value === modelVersion;
          return (
            <button
              key={item.value}
              type="button"
              className={cn(
                'flex min-h-[58px] items-center gap-3 rounded-10 border border-transparent px-5 py-3 text-left transition-colors',
                active && 'bg-white/[0.12]',
              )}
              onClick={() => handleModelVersionChange(item.value)}
            >
              <ModelVersionSelectItemContent
                image={item.image}
                title={item.title}
                description={item.description}
                isNew={item.isNew}
                className="min-w-0 flex-1"
              />
              {active ? <Check className="size-4 shrink-0 text-system-white" /> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};
